import React, { useRef, forwardRef, RefObject } from "react";
import { PercentInput } from "src/app/components/MaskedInputs/PercentInput";
import { SeperatedNumberInput } from "src/app/components/MaskedInputs/SeperatedNumberInput";
import { CurrencyInput } from "src/app/components/MaskedInputs/CurrencyInput";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import useOnClickOutside from "src/app/methods/useOnClickOutside";
import { useCellCurrencyState } from "src/hooks/useCellCurrencyState";

interface ValueInputWrapperProps {
  value: string;
  onChange: (newValue: string, customCurrencyCode?: string | null) => void;
  onBlur?: (newValue: string) => void;
  onFocus?: () => void;
  type: MetaFieldType.Number | MetaFieldType.Currency | MetaFieldType.Percent;
  handleUseOnClickOutside?: () => void;
  readOnly: boolean;
  columnCurrencyCode?: string;
  customCurrencyCode?: string;
  customCurrencyValue?: string;
}

export const ValueInputWrapper = forwardRef<
  HTMLInputElement,
  ValueInputWrapperProps
>(
  (
    {
      value,
      onChange,
      onBlur,
      onFocus,
      type,
      handleUseOnClickOutside,
      readOnly,
      columnCurrencyCode,
      customCurrencyCode,
      customCurrencyValue,
    },
    ref,
  ) => {
    const {
      value: localValue,
      setValue: setLocalValue,
      cellCurrencyCode,
      hasCustomCurrency,
      handleCurrencyPickerSave,
    } = useCellCurrencyState(value, {
      currencyCode: columnCurrencyCode,
      customCurrencyCode,
      customCurrencyValue,
    });
    const isCurrencyPickerOpenRef = useRef(false);

    const refObject = ref as RefObject<HTMLInputElement>;

    useOnClickOutside(refObject, (event) => {
      if (isCurrencyPickerOpenRef.current) return;
      // the currency code is part of the editor: clicking it must not end editing
      if (
        (event.target as HTMLElement).closest?.(".currency-code--interactive")
      )
        return;
      if (handleUseOnClickOutside) {
        handleUseOnClickOutside();
        if (refObject.current) {
          refObject.current.blur();
        }
      }
    });

    const handleBlur = () => {
      if (isCurrencyPickerOpenRef.current) return;
      if (onBlur) {
        onBlur(localValue);
      }
      onChange(
        localValue,
        type === MetaFieldType.Currency && hasCustomCurrency
          ? cellCurrencyCode
          : undefined,
      );
    };

    switch (type) {
      case MetaFieldType.Number:
        return (
          <SeperatedNumberInput
            inputRef={refObject}
            className="value-input"
            rawValue={localValue}
            setRawValue={setLocalValue}
            onBlur={handleBlur}
            onFocus={onFocus}
            readOnly={readOnly}
          />
        );
      case MetaFieldType.Currency:
        return (
          <CurrencyInput
            inputRef={refObject}
            className="value-input"
            rawValue={localValue}
            setRawValue={setLocalValue}
            onBlur={handleBlur}
            onFocus={onFocus}
            readOnly={readOnly}
            prefix={cellCurrencyCode}
            columnCurrencyCode={columnCurrencyCode || ""}
            onCurrencyChange={handleCurrencyPickerSave}
            onCurrencyPickerOpenChange={(isOpen: boolean) => {
              isCurrencyPickerOpenRef.current = isOpen;
            }}
          />
        );
      case MetaFieldType.Percent:
        return (
          <PercentInput
            inputRef={refObject}
            className="value-input"
            rawValue={localValue}
            setRawValue={setLocalValue}
            onBlur={handleBlur}
            onFocus={onFocus}
            readOnly={readOnly}
          />
        );
      default:
        return null;
    }
  },
);
